import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { GooglePlaceAutocompleteInput } from 'restaurantAdmin/components/googlePlaces/GooglePlaceAutocompleteInput';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import styles from './EventInformationStep.scss';
import type { EventFormData, ServiceFeeFormItem } from '../RestaurantEventForm';
import { ControlledFormPercentageInput } from '@components/formInputs/ControlledFormPercentageInput';
import { ControlledFormTimeRange } from '@components/formInputs/ControlledFormTimeRange';
import cx from 'classnames';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import { IconButton } from '@components/iconButton/IconButton';

const DEFAULT_SERVICE_FEE: ServiceFeeFormItem = {
  label: '',
  percentage: '',
};

export const EventInformationStep = ({
  control,
  setValue,
}: {
  control: Control<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
}) => {
  const restaurant = useRestaurant();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'serviceFees',
  });

  const serviceFeeRemoveTooltipBase = useHtmlId('event-service-fee-remove');
  const serviceFeeAddTooltipBase = useHtmlId('event-service-fee-add');

  return (
    <section className={cx(styles.eventFormStep, styles.eventInformationStep)}>
      <ControlledFormInput<EventFormData>
        control={control}
        name="name"
        type="text"
        label="Name"
        placeholder="Name"
        rules={{ required: 'Name is required' }}
        labelClassName={styles.fieldLabel}
        darkMode
      />

      <div className={styles.formRow}>
        <ControlledFormInput<EventFormData>
          control={control}
          name="startDate"
          type="date"
          label="Date"
          placeholder="Select date"
          rules={{ required: 'Date is required' }}
          labelClassName={styles.fieldLabel}
          darkMode
        />
        <ControlledFormTimeRange<EventFormData>
          control={control}
          groupLabel=""
          fromProps={{
            label: '',
            name: 'startTime',
            rules: { required: 'Start time is required' },
          }}
          toProps={{
            label: '',
            name: 'endTime',
            rules: { required: 'End time is required' },
          }}
          darkMode
        />
      </div>

      <GooglePlaceAutocompleteInput<EventFormData>
        control={control}
        setValue={setValue}
        name="location"
        label="Location"
        placeholder="Search for an address or venue"
        labelClassName={styles.fieldLabel}
        darkMode
        restaurantId={restaurant.id}
      />

      <ControlledFormTextareaAutosize<EventFormData>
        control={control}
        name="directions"
        label="Directions"
        placeholder="Directions"
        minRows={3}
        labelClassName={styles.fieldLabel}
        ariaLabel="Directions"
        darkMode
      />

      <ControlledFormTextareaAutosize<EventFormData>
        control={control}
        name="description"
        label="Description"
        placeholder="Description"
        minRows={4}
        labelClassName={styles.fieldLabel}
        ariaLabel="Description"
        darkMode
      />

      <div className={styles.fieldLabel}>Service fees</div>

      <div className={cx(styles.formCardRow, styles.bottomRow)}>
        <SimpleBar className={styles.serviceFeesPercentagesList}>
          {fields.length > 0 &&
            fields.map((field, index) => (
              <div key={field.id} className={styles.serviceFeeRow}>
                <div className={styles.labelInput}>
                  <ControlledFormInput<EventFormData>
                    control={control}
                    name={`serviceFees.${index}.label`}
                    type="text"
                    label="Name"
                    placeholder="e.g. Facilities Fee"
                    labelClassName={styles.fieldLabel}
                    darkMode
                  />
                </div>
                <div className={styles.percentageInput}>
                  <ControlledFormPercentageInput<EventFormData>
                    control={control}
                    name={`serviceFees.${index}.percentage`}
                    label="Percentage (%)"
                    labelClassName={styles.fieldLabel}
                    className={styles.serviceFeePercentageInput}
                    darkMode
                  />
                </div>
                {fields.length > 1 && (
                  <IconButton
                    ariaLabel="Remove service fee"
                    className={cx(
                      styles.eventModalActionIconButton,
                      styles.eventModalActionIconButtonDefaultStroke,
                      styles.deleteButton,
                    )}
                    dataTooltipId={`${serviceFeeRemoveTooltipBase}-${field.id}`}
                    iconName="trash"
                    onClick={() => remove(index)}
                    tooltipContent="Remove service fee"
                  />
                )}
                <IconButton
                  ariaLabel="Add service fee"
                  className={cx(
                    styles.eventModalActionIconButton,
                    styles.eventModalActionIconButtonDefaultStroke,
                    styles.addServiceFeeButton,
                    styles.iconButtonDefaultStroke,
                  )}
                  dataTooltipId={`${serviceFeeAddTooltipBase}-${field.id}`}
                  iconName="plus"
                  onClick={() => append(DEFAULT_SERVICE_FEE)}
                  tooltipContent="Add service fee"
                />
              </div>
            ))}
        </SimpleBar>
      </div>
    </section>
  );
};
